/**
 * What a node on the web canvas would run — the missing half of the bridge between the two
 * node worlds.
 *
 * The web canvas names its nodes after what the person is doing (an Image node set to
 * `tools` / `background-removal`); the runtime names them after the operation (`edit.rembg`).
 * `webPageToStudioGraph` keeps web nodes as `web:<Type>` so a page round-trips without loss,
 * which also means nothing downstream can tell whether a page is runnable. This table answers
 * that: given a node's type, purpose and sub-purpose, which operation from the catalogue it is,
 * and which built-in app is the same capability.
 *
 * It is keyed on the model registry's own taxonomy (`pools/ai/model-sub-purpose.schema`), not
 * on React component names, so both sides of the product can read it. It deliberately imports
 * nothing but the catalogue: this module is vendored into the desktop engine on its own.
 * `__tests__/web-operations.test.ts` holds it against the taxonomy — every sub-purpose there is
 * either mapped here or listed in `WEB_NODES_WITHOUT_OPERATION`, so a new sub-purpose cannot
 * quietly become a node no runtime knows how to run.
 */
import { GRAPH_NODE_DEFS } from './node-defs';

export interface WebNodeOperation {
  /** A key of GRAPH_NODE_DEFS. */
  operation: string;
  /** The built-in app that is the same capability, as a catalogue slug (no `genie.studio.` prefix). */
  app?: string;
}

/** The web canvas's React component names, as the model-registry node types they stand for. */
export const WEB_NODE_TYPES: Record<string, string> = {
  ImageNode: 'image',
  VideoNode: 'video',
  ThreeDNode: '3d',
  TextNode: 'text-llm',
};

/**
 * The web's Canvas In and Canvas Out. These two carry no purpose: a frame goes in, a version
 * comes out. They are how a workflow reaches the artboard at all.
 */
export const WEB_CANVAS_NODES: Record<string, string> = {
  FrameInput: 'canvas.in',
  FrameOutput: 'canvas.out',
};

const KEY = (nodeType: string, purpose: string, subPurpose?: string) =>
  subPurpose ? `${nodeType}/${purpose}/${subPurpose}` : `${nodeType}/${purpose}`;

const MAP: Record<string, WebNodeOperation> = {
  // ── Image ────────────────────────────────────────────────────────────────
  [KEY('image', 'create')]: { operation: 'gen.image', app: 'generate' },
  [KEY('image', 'edit')]: { operation: 'edit.instruct', app: 'image-edit' },
  [KEY('image', 'enhance')]: { operation: 'edit.upscale', app: 'upscale' },
  [KEY('image', 'enhance', 'upscale')]: { operation: 'edit.upscale', app: 'upscale' },
  [KEY('image', 'enhance', 'super-resolution')]: { operation: 'edit.upscale', app: 'upscale' },
  [KEY('image', 'enhance', 'sharpen')]: { operation: 'edit.adjust', app: 'sharpen' },
  [KEY('image', 'tools', 'inpaint')]: { operation: 'edit.inpaint', app: 'inpaint' },
  [KEY('image', 'tools', 'outpaint')]: { operation: 'edit.expand', app: 'expand' },
  [KEY('image', 'tools', 'background-removal')]: { operation: 'edit.rembg', app: 'remove-background' },
  [KEY('image', 'tools', 'object-removal')]: { operation: 'edit.erase', app: 'erase' },
  [KEY('image', 'tools', 'segmentation')]: { operation: 'edit.segment', app: 'segment' },
  [KEY('image', 'tools', 'captioning')]: { operation: 'text.describe', app: 'describe' },
  [KEY('image', 'tools', 'vectorization')]: { operation: 'edit.vectorize', app: 'vectorize' },
  [KEY('image', 'tools', 'face-retouching')]: { operation: 'edit.detail', app: 'enhance' },
  [KEY('image', 'tools', 'style-transfer')]: { operation: 'edit.instruct', app: 'styler' },
  [KEY('image', 'tools', 'reimagine')]: { operation: 'tool.run', app: 'reimagine' },
  [KEY('image', 'tools', 'colorization')]: { operation: 'tool.run', app: 'colorize' },
  [KEY('image', 'tools', 'relighting')]: { operation: 'tool.run', app: 'relight' },
  [KEY('image', 'tools', 'virtual-try-on')]: { operation: 'tool.run', app: 'virtual-try-on' },
  [KEY('image', 'tools', 'product-photography')]: { operation: 'tool.run', app: 'product-photo' },
  [KEY('image', 'tools', 'depth-estimation')]: { operation: 'tool.run', app: 'depth-map' },
  [KEY('image', 'tools', 'pose-estimation')]: { operation: 'tool.run', app: 'pose-map' },
  [KEY('image', 'tools', 'edge-detection')]: { operation: 'tool.run', app: 'edge-map' },

  // ── Video ────────────────────────────────────────────────────────────────
  [KEY('video', 'create')]: { operation: 'gen.video', app: 'animate' },
  [KEY('video', 'create', 'text-to-video')]: { operation: 'gen.video', app: 'animate' },
  [KEY('video', 'create', 'image-to-video')]: { operation: 'gen.video', app: 'animate' },
  [KEY('video', 'edit')]: { operation: 'tool.run', app: 'edit-video' },
  [KEY('video', 'enhance')]: { operation: 'tool.run', app: 'upscale-video' },
  [KEY('video', 'enhance', 'video-upscale')]: { operation: 'tool.run', app: 'upscale-video' },
  [KEY('video', 'tools', 'frame-interpolation')]: { operation: 'edit.interpolate', app: 'smooth-video' },
  [KEY('video', 'tools', 'lipsync')]: { operation: 'tool.run', app: 'lipsync' },
  [KEY('video', 'tools', 'video-extend')]: { operation: 'tool.run', app: 'extend-video' },
  [KEY('video', 'tools', 'reframe')]: { operation: 'tool.run', app: 'reframe-video' },
  [KEY('video', 'tools', 'motion-transfer')]: { operation: 'tool.run', app: 'motion-transfer' },
  [KEY('video', 'tools', 'video-captioning')]: { operation: 'tool.run', app: 'describe-video' },

  // ── Sound ────────────────────────────────────────────────────────────────
  [KEY('sound', 'create')]: { operation: 'gen.sound', app: 'sound' },
  [KEY('sound', 'create', 'text-to-music')]: { operation: 'gen.sound', app: 'sound' },
  [KEY('sound', 'create', 'text-to-sound')]: { operation: 'gen.sound', app: 'sound' },
  [KEY('sound', 'enhance', 'audio-enhance')]: { operation: 'tool.run', app: 'clean-audio' },
  [KEY('sound', 'tools', 'transcription')]: { operation: 'tool.run', app: 'transcribe' },
  [KEY('sound', 'tools', 'voice-conversion')]: { operation: 'tool.run', app: 'change-voice' },
  [KEY('sound', 'tools', 'audio-analysis')]: { operation: 'tool.run', app: 'analyze-audio' },

  // ── 3D ───────────────────────────────────────────────────────────────────
  [KEY('3d', 'create')]: { operation: 'gen.3d', app: 'image-to-3d' },
  [KEY('3d', 'create', 'image-to-3d')]: { operation: 'gen.3d', app: 'image-to-3d' },
  // Text to 3D reaches the same operation without an image; no app offers it yet.
  [KEY('3d', 'create', 'text-to-3d')]: { operation: 'gen.3d' },
  [KEY('3d', 'edit', 'remesh')]: { operation: 'tool.run', app: 'remesh' },
  [KEY('3d', 'edit', 'retexture')]: { operation: 'tool.run', app: 'retexture' },

  // ── Text ─────────────────────────────────────────────────────────────────
  [KEY('text-llm', 'create')]: { operation: 'text.enhance', app: 'refine' },
  [KEY('text-llm', 'edit')]: { operation: 'text.enhance', app: 'refine' },
};

/**
 * Sub-purposes that are deliberately not operations, and why. These reach an editor, a training
 * job or a session surface — things a single DAG node cannot stand for. Listing them is what
 * lets the tests prove the taxonomy is covered rather than merely sampled.
 */
export const WEB_NODES_WITHOUT_OPERATION: Record<string, string> = {
  'image/tools/controlnet': 'A guidance input to another operation, not an operation.',
  'image/tools/mask': 'Its own editor; the mask it produces is an input.',
  'image/tools/face-swap': 'No operation and no app offers it yet.',
  'image/tools/training': 'Starts a training job, which outlives a graph run.',
  'image/enhance/denoise': 'No operation offers it on its own yet.',
  'image/enhance/restore': 'No operation offers it on its own yet.',
  'image/enhance/hdr': 'No operation offers it on its own yet.',
  'video/tools/frame-extraction': 'A player action on a clip, not a model run.',
  'video/tools/video-segmentation': 'No operation offers it yet.',
  'video/tools/ffmpeg': 'A container and codec utility, not a model run.',
  'video/tools/training': 'Starts a training job, which outlives a graph run.',
  'sound/create/text-to-speech': 'No operation offers a voice yet; generation covers music and sound.',
  'sound/tools/waveform': 'A display of a clip, not a model run.',
  '3d/edit/part-editing': 'Its own editor.',
};

/**
 * The operation a node would run, or null when it has none.
 *
 * `nodeType` accepts either a registry node type (`image`) or the web canvas's component name
 * (`ImageNode`). A sub-purpose this build has not mapped falls back to the node's bare purpose,
 * so a node configured with a newer sub-purpose still runs its category's operation rather than
 * becoming unrunnable — except where the sub-purpose is listed as deliberately not an operation.
 */
export function webNodeOperation(
  nodeType: string,
  purpose?: string,
  subPurpose?: string
): WebNodeOperation | null {
  if (Object.hasOwn(WEB_CANVAS_NODES, nodeType)) return { operation: WEB_CANVAS_NODES[nodeType]! };
  const type = Object.hasOwn(WEB_NODE_TYPES, nodeType) ? WEB_NODE_TYPES[nodeType]! : nodeType;
  if (!purpose) return null;
  if (subPurpose) {
    const exact = KEY(type, purpose, subPurpose);
    if (Object.hasOwn(WEB_NODES_WITHOUT_OPERATION, exact)) return null;
    if (Object.hasOwn(MAP, exact)) return MAP[exact]!;
  }
  const bare = KEY(type, purpose);
  return Object.hasOwn(MAP, bare) ? MAP[bare]! : null;
}

/** Every mapping, for tests and for a host listing what a build can run. */
export const WEB_NODE_OPERATIONS: Readonly<Record<string, WebNodeOperation>> = MAP;

/** Does the catalogue actually define everything this table points at? */
export function webOperationIssues(): string[] {
  return Object.entries(MAP)
    .filter(([, value]) => !Object.hasOwn(GRAPH_NODE_DEFS, value.operation))
    .map(([key, value]) => `${key} names an unavailable operation: ${value.operation}.`);
}
